/**
 * Relationship Resolver
 *
 * Determines the relationship between a user and a resource (accession).
 * The relationship drives which permissions apply via PERMISSION_RULES.
 */

export type Relationship =
  | "at_owning_lab"
  | "at_other_lab"
  | "at_ordering_org"
  | "at_allowed_org"
  | "at_any";

export type RelationshipUser = {
  org: number;
  role: string;
  allowedOrgs: number[];
};

export type RelationshipResource = {
  receivingOrgId?: number | null;
  orgId?: number | null;
};

/**
 * Resolve the relationship between a user and a resource.
 *
 * For lab roles: at_owning_lab (home) or at_other_lab (everything else).
 * For org roles: at_ordering_org (home) or at_allowed_org (allowedOrgs).
 * Managers and admins: at_any (unrestricted).
 *
 * Returns null when no relationship exists (no access).
 */
export function resolveRelationship(
  user: RelationshipUser,
  resource: RelationshipResource,
): Relationship | null {
  const { role } = user;

  // Managers and admins — unrestricted
  if (role === "blis_lab_manager" || role === "blis_lab_admin") {
    return "at_any";
  }

  // Lab roles — relationship based on receivingOrgId
  if (role.startsWith("blis_lab")) {
    if (resource.receivingOrgId == null) return "at_other_lab";
    if (resource.receivingOrgId === user.org) return "at_owning_lab";
    return "at_other_lab";
  }

  // Org roles — relationship based on orgId
  if (role.startsWith("blis_org")) {
    if (resource.orgId == null) return null;
    if (resource.orgId === user.org) return "at_ordering_org";
    if (user.allowedOrgs.includes(resource.orgId)) return "at_allowed_org";
    return null;
  }

  return null;
}

/**
 * Get all relationships a user could have, and which org IDs they apply to.
 * Used for building query filters (e.g., "which orgs can this user read?").
 */
export function resolveAllRelationships(
  user: RelationshipUser,
): { relationship: Relationship; orgIds: number[] | "all" }[] {
  const { role } = user;
  const results: { relationship: Relationship; orgIds: number[] | "all" }[] =
    [];

  // Managers and admins — unrestricted
  if (role === "blis_lab_manager" || role === "blis_lab_admin") {
    results.push({ relationship: "at_any", orgIds: "all" });
    return results;
  }

  // Lab roles: home lab + all other labs
  if (role.startsWith("blis_lab")) {
    results.push({ relationship: "at_owning_lab", orgIds: [user.org] });
    results.push({ relationship: "at_other_lab", orgIds: "all" });
    return results;
  }

  // Org roles: home org + allowed orgs
  if (role.startsWith("blis_org")) {
    results.push({ relationship: "at_ordering_org", orgIds: [user.org] });
    if (user.allowedOrgs.length > 0) {
      results.push({
        relationship: "at_allowed_org",
        orgIds: [...user.allowedOrgs],
      });
    }
    return results;
  }

  return results;
}
